// Мастер регистрации: формат → заведение → владелец → код кассы.
// Три шага, потому что человек с улицы не должен думать дольше минуты.
import { useState } from 'react';
import { SignupRequest, SignupResponse, type Vertical } from '@dastarhan/contracts';

const API = '/api/v1';

const VERTICALS: { v: Vertical; title: string; hint: string }[] = [
  { v: 'CAFE', title: 'Кафе / ресторан', hint: 'кухня, залы, столы, брони' },
  { v: 'FASTFOOD', title: 'Фастфуд', hint: 'кухня есть, рассадки нет' },
  { v: 'SHOP', title: 'Магазин', hint: 'быстрые продажи без кухни' },
  { v: 'SALON', title: 'Салон', hint: 'услуги, без кухни и столов' },
  { v: 'BILLIARD', title: 'Бильярд / досуг', hint: 'столы и бар' },
];

export function SignupScreen({ onDone, onBack }: {
  onDone: (access: string) => void;
  onBack: () => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [vertical, setVertical] = useState<Vertical>('CAFE');
  const [accountName, setAccountName] = useState('');
  const [locationName, setLocationName] = useState('Основная точка');
  const [ownerName, setOwnerName] = useState('');
  // Телефон — ключ живого человека: по нему платформа ловит дубли
  const [ownerPhone, setOwnerPhone] = useState('');
  const [city, setCity] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ownerPin, setOwnerPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<{ access: string; code: string } | null>(null);

  const submit = async () => {
    setErr('');
    const body = SignupRequest.safeParse({
      accountName: accountName.trim(),
      vertical,
      locationName: locationName.trim() || 'Основная точка',
      ownerName: ownerName.trim(),
      email: email.trim(),
      password,
      ownerPin,
      ownerPhone,
      ...(city.trim() ? { city: city.trim() } : {}),
    });
    if (!body.success) { setErr('Проверьте поля: пароль от 8 символов, PIN — 4 цифры.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body.data),
      });
      if (res.status === 409) { setErr('Эта почта уже занята — попробуйте войти.'); return; }
      if (res.status === 400) {
        const d = await res.json().catch(() => null) as { message?: string } | null;
        setErr(d?.message ?? 'Проверьте телефон: возможно, заявка уже отправлена.');
        return;
      }
      if (!res.ok) { setErr('Не получилось создать заведение. Попробуйте ещё раз.'); return; }
      const d = SignupResponse.parse(await res.json());
      setDone({ access: d.tokens.access, code: d.activationCode });
    } catch { setErr('Нет связи с сервером.'); }
    finally { setBusy(false); }
  };

  if (done) {
    /*
     * Заявка отправлена — но вход откроется после решения человека.
     *
     * Раньше здесь сразу открывался бэк-офис. Теперь платформа
     * смотрит каждую регистрацию: живое заведение или чья-то проба.
     * Код активации кассы всё равно показываем — он показывается
     * один раз, и потерять его из-за ожидания было бы обидно.
     */
    return (
      <div className="login wizard">
        <span className="eyebrow">Заявка отправлена</span>
        <h1>Ждём одобрения</h1>
        <p className="wizard-hint">
          Мы проверяем заявку и свяжемся с вами — обычно это занимает
          несколько часов. После одобрения вход откроется этой же почтой
          и паролем, а заведение получит пробный период.
        </p>
        <div className="code-card">
          <span>Код активации кассы — сохраните, он показывается один раз</span>
          <b>{done.code}</b>
        </div>
        <p className="wizard-hint">
          Когда доступ откроется: запустите кассу на планшете, введите этот
          код, затем ваш PIN <b>{ownerPin}</b>.
        </p>
        <button className="btn" style={{ width: 'min(340px, 90vw)' }}
          onClick={() => window.location.reload()}>На страницу входа</button>
      </div>
    );
  }

  return (
    <div className="login wizard">
      <span className="eyebrow">Новое заведение · шаг {step} из 3</span>

      {step === 1 && (
        <>
          <h1>Что у вас за бизнес?</h1>
          <div className="vert-cards" role="radiogroup" aria-label="Формат бизнеса">
            {VERTICALS.map((x) => (
              <button key={x.v} className={`vert-card ${vertical === x.v ? 'on' : ''}`}
                role="radio" aria-checked={vertical === x.v}
                onClick={() => setVertical(x.v)}>
                <b>{x.title}</b>
                <span>{x.hint}</span>
              </button>
            ))}
          </div>
          <p className="wizard-hint">Формат можно сменить в любой момент — система перестроится сама.</p>
          <div className="wizard-nav">
            <button className="btn" onClick={onBack}>← Ко входу</button>
            <button className="btn accent" onClick={() => setStep(2)}>Дальше</button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h1>Как называется заведение?</h1>
          <label>Название
            <input autoFocus value={accountName} placeholder="Кафе «Дастархан»"
              onChange={(e) => setAccountName(e.target.value)} />
          </label>
          <label>Точка (филиал)
            <input value={locationName}
              onChange={(e) => setLocationName(e.target.value)} />
          </label>
          <div className="wizard-nav">
            <button className="btn" onClick={() => setStep(1)}>← Назад</button>
            <button className="btn accent" disabled={accountName.trim().length < 2}
              onClick={() => setStep(3)}>Дальше</button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <h1>Владелец</h1>
          <label>Ваше имя
            <input autoFocus value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
          </label>
          <label>Телефон
            <input inputMode="tel" placeholder="+7 777 000 00 00" value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)} />
          </label>
          <label>Город
            <input placeholder="Туркестан" value={city}
              onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>Почта — вход в бэк-офис
            <input type="email" autoComplete="username" value={email}
              onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>Пароль (от 8 символов)
            <input type="password" autoComplete="new-password" value={password}
              onChange={(e) => setPassword(e.target.value)} />
          </label>
          <label>Ваш PIN на кассе — 4 цифры
            <input inputMode="numeric" maxLength={4} value={ownerPin}
              onChange={(e) => setOwnerPin(e.target.value.replace(/\D/g, '').slice(0, 4))} />
          </label>
          <p className="error">{err}</p>
          <div className="wizard-nav">
            <button className="btn" onClick={() => setStep(2)}>← Назад</button>
            <button className="btn accent" disabled={busy} onClick={submit}>
              {busy ? 'Создаём…' : 'Создать заведение'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
